import { create } from 'zustand';
import useGameState from '../engine/gameState';
import { announceNewInstalls, getInstalledApps } from '../engine/installedApps';
import * as core from '../services/coreClient';
import { COPY } from '../content/copy';
import { trackEvent } from '../utils/analytics';

// In-flight guard for loadLoans: collapses concurrent reads (e.g. the Vinnie
// view auto-loading twice in the same frame) into a single network request.
let loansLoading = null;

const TOKEN_KEY = 'dcw_core_token';
const PLAYER_KEY = 'dcw_core_player';
const RUN_KEY = 'dcw_core_run';

const RUN_TURNS = 30;

let bootPromise = null;

function readStorage(key) {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key, value) {
  if (typeof window === 'undefined') return;
  try {
    if (value == null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, String(value));
  } catch {
    /* storage can be unavailable in private mode */
  }
}

function readStoredRun() {
  const raw = readStorage(RUN_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function persistSession(playerId, token) {
  writeStorage(PLAYER_KEY, playerId);
  writeStorage(TOKEN_KEY, token);
}

function clearStoredSession() {
  writeStorage(TOKEN_KEY, null);
  writeStorage(PLAYER_KEY, null);
  writeStorage(RUN_KEY, null);
}

export function moneyAmount(money) {
  const value = Number(money?.amount ?? money ?? 0);
  return Number.isFinite(value) ? value : 0;
}

export function formatMinor(money) {
  const minor = Number(money?.amountMinor ?? 0);
  if (!Number.isFinite(minor)) return '--';
  const sign = minor < 0 ? '-' : '';
  const absolute = Math.abs(minor);
  return `${sign}$${Math.floor(absolute / 100)}.${String(absolute % 100).padStart(2, '0')}`;
}

export function normalizeRun(run) {
  if (!run) return null;
  const turnsRemaining = Number(run.turnsRemaining ?? run.turns_remaining);
  const day = Number.isFinite(Number(run.day))
    ? Number(run.day)
    : Number.isFinite(turnsRemaining)
      ? RUN_TURNS - turnsRemaining + 1
      : null;
  return {
    ...run,
    sessionId: run.sessionId ?? run.session_id ?? null,
    turnsRemaining: Number.isFinite(turnsRemaining) ? turnsRemaining : null,
    day: Number.isFinite(day) ? day : null,
    status: run.status || 'active',
  };
}

function syncGameState(state, run, status, playerId) {
  if (typeof useGameState?.getState !== 'function') return;
  const cash = moneyAmount(state?.cash);
  const debt = moneyAmount(state?.debt);
  useGameState.getState().applyCoreState({
    status,
    playerId: playerId || state?.playerId || null,
    cash,
    debt,
    heat: Number(state?.heat ?? 0),
    districtId: state?.districtId || null,
    reputationPoints: Number(state?.reputationPoints ?? 0),
    inventoryCapacity: Number(state?.inventoryCapacity ?? 0),
    inventory: Array.isArray(state?.inventory) ? state.inventory : [],
    version: Number(state?.version ?? 0),
    day: state?.day ?? run?.day ?? null,
    turnsRemaining: run?.turnsRemaining ?? null,
    updatedAt: state?.updatedAt || null,
    // Provided by the backend once V2-L8a lands; undefined until then.
    ...(Array.isArray(state?.installedApps) ? { installedApps: state.installedApps } : {}),
  });
}

export function coreErrorMessage(error) {
  if (!error) return 'LINE DOWN';
  if (error.code) return `${error.code} // ${error.message}`;
  return error.message || 'LINE DOWN';
}

export const useCoreSession = create((set, get) => ({
  status: 'idle',
  playerId: null,
  token: null,
  state: null,
  run: null,
  content: null,
  receipt: null,
  ticket: null,
  drops: [],
  claims: [],
  fragments: [],
  error: null,
  loading: false,
  lastSync: null,

  setReceipt: (receipt) => set({ receipt }),
  clearReceipt: () => set({ receipt: null }),

  loadFragments: async () => {
    const { token } = get();
    if (!token) return [];
    const fragments = await core.listFragments(token);
    set({ fragments });
    return fragments;
  },

  applyState: (state, run = get().run) => {
    if (!state) return;
    const hasTicket = Object.prototype.hasOwnProperty.call(state, 'ticket');
    set({
      state,
      lastSync: Date.now(),
      ...(hasTicket ? { ticket: state.ticket ?? null } : {}),
    });
    syncGameState(state, run, 'online', get().playerId);
  },

  applyRun: (run) => {
    const current = get().run;
    const normalized = normalizeRun({
      ...current,
      ...run,
      sessionId: run?.sessionId ?? run?.session_id ?? current?.sessionId ?? null,
    });
    if (!normalized) return;
    set({ run: normalized });
    writeStorage(RUN_KEY, JSON.stringify(normalized));
    syncGameState(get().state, normalized, get().status, get().playerId);
  },

  ensureRun: async () => {
    const { token, run } = get();
    if (!token) return null;
    if (run?.turnsRemaining != null) return run;
    try {
      const started = await core.startRun(token);
      get().applyRun(started);
      return get().run;
    } catch (error) {
      if (error?.status === 409) {
        const stored = readStoredRun();
        if (stored) {
          get().applyRun(stored);
          return get().run;
        }
        set({ run: { status: 'active', turnsRemaining: null, day: null } });
        return get().run;
      }
      if (error?.isAuthError) throw error;
      return null;
    }
  },

  ensureSession: async () => {
    const current = get();
    if (current.status === 'online' && current.token) return current;
    if (bootPromise) return bootPromise;

    bootPromise = (async () => {
      set({ status: 'authing', error: null });
      const storedToken = readStorage(TOKEN_KEY);
      const storedPlayer = readStorage(PLAYER_KEY);

      try {
        if (storedToken) {
          try {
            const state = await core.getState(storedToken);
            set({ token: storedToken, playerId: state?.playerId || storedPlayer, status: 'online' });
            get().applyState(state, readStoredRun());
            await get().ensureRun();
            return get();
          } catch (error) {
            if (!error?.isAuthError) throw error;
            clearStoredSession();
          }
        }

        const guest = await core.createGuest();
        const token = guest?.token;
        const playerId = guest?.playerId;
        if (!token || !playerId) throw new Error('Guest session returned no token');
        persistSession(playerId, token);
        set({ token, playerId, status: 'online', error: null });
        const state = await core.getState(token);
        get().applyState(state, null);
        await get().ensureRun();
        return get();
      } catch (error) {
        set({ status: 'offline', error: coreErrorMessage(error) });
        throw error;
      }
    })();

    try {
      return await bootPromise;
    } finally {
      bootPromise = null;
    }
  },

  refreshState: async () => {
    await get().ensureSession();
    const { token } = get();
    if (!token) return null;
    try {
      const state = await core.getState(token);
      get().applyState(state);
      return state;
    } catch (error) {
      if (error?.isAuthError) {
        clearStoredSession();
        set({ status: 'idle', token: null, playerId: null, state: null, run: null });
        return get().ensureSession();
      }
      set({ status: 'offline', error: coreErrorMessage(error) });
      throw error;
    }
  },

  loadContent: async () => {
    if (get().content) return get().content;
    const content = await core.getContentPack();
    set({ content });
    return content;
  },

  quoteTrade: async (assetId, side, quantity = 1) => {
    await get().ensureSession();
    return core.getQuote(get().token, assetId, side, quantity);
  },

  loadTicket: async () => {
    await get().ensureSession();
    const { token } = get();
    if (!token) return null;
    const payload = await core.getTicket(token);
    const ticket = payload?.ticket ?? null;
    set({ ticket });
    return ticket;
  },

  buyTicket: async (toDistrictId) => {
    await get().ensureSession();
    set({ loading: true, error: null });
    try {
      const result = await core.buyTicket(
        get().token,
        toDistrictId,
        core.newIdempotencyKey('ticket'),
      );
      const ticket = result?.ticket ?? null;
      set({
        ticket,
        loading: false,
        receipt: {
          kind: 'ticket',
          status: 'ok',
          title: 'TICKET ISSUED',
          detail: `NEXT DAY // ${String(toDistrictId || '--').toUpperCase()}`,
          payload: ticket,
        },
      });
      // The ticket endpoint returns the ticket only; refresh the state the
      // shell reads for cash. A failed refresh must not hide the ticket.
      try {
        const state = await core.getState(get().token);
        get().applyState(state);
      } catch (refreshError) {
        set({ error: coreErrorMessage(refreshError) });
      }
      return ticket;
    } catch (error) {
      set({ error: coreErrorMessage(error), loading: false });
      throw error;
    }
  },

  executeTrade: async (assetId, side, quantity = 1) => {
    await get().ensureSession();
    set({ loading: true, error: null });
    try {
      const result = await core.executeTrade(
        get().token,
        { assetId, side, quantity },
        core.newIdempotencyKey('trade'),
      );
      get().applyState(result.state);
      const receipt = {
        kind: 'trade',
        status: 'ok',
        title: `${side.toUpperCase()} ${quantity} ${assetId.toUpperCase()}`,
        detail: `${side === 'buy' ? COPY.trade.buy : COPY.trade.sell} // ${result.trade?.total?.amount ?? '--'} DCW_USD`,
        payload: result.trade,
      };
      set({ receipt, loading: false });
      return result;
    } catch (error) {
      set({ error: coreErrorMessage(error), loading: false });
      throw error;
    }
  },

  executeTravel: async (toDistrictId) => {
    await get().ensureSession();
    set({ loading: true, error: null });
    try {
      const result = await core.executeTravel(
        get().token,
        toDistrictId,
        core.newIdempotencyKey('travel'),
      );
      get().applyState(result.state, result.run || get().run);
      if (result.run) get().applyRun(result.run);
      const receipt = {
        kind: 'travel',
        status: 'ok',
        title: `TRAVEL ${toDistrictId.toUpperCase()}`,
        detail: `${COPY.travel.arrived} // DAY ${result.run?.day ?? '--'}`,
        payload: result.travel,
      };
      trackEvent('travel_confirm', { districtId: toDistrictId, source: 'core_session' });
      set({ receipt, loading: false });
      return result;
    } catch (error) {
      set({ error: coreErrorMessage(error), loading: false });
      throw error;
    }
  },

  waitTurn: async () => {
    await get().ensureSession();
    const active = get().run || (await get().ensureRun());
    if (!active?.sessionId) {
      const error = new Error('NO ACTIVE RUN');
      set({ error: coreErrorMessage(error), loading: false });
      throw error;
    }
    set({ loading: true, error: null });
    try {
      const result = await core.waitTurn(get().token, active.sessionId);
      const state = await core.getState(get().token);
      const run = {
        ...result,
        day: Number.isFinite(Number(result.day))
          ? Number(result.day)
          : Number.isFinite(Number(result.turnsRemaining))
            ? RUN_TURNS - Number(result.turnsRemaining) + 1
            : null,
      };
      get().applyState(state, run);
      get().applyRun(run);
      set({
        receipt: {
          kind: 'wait',
          status: 'ok',
          title: 'LAY LOW',
          detail: `DAY ${run.day ?? '--'}`,
          payload: result,
        },
        loading: false,
      });
      return result;
    } catch (error) {
      set({ error: coreErrorMessage(error), loading: false });
      throw error;
    }
  },

  loadLoans: async () => {
    // Idempotent refresh: a read already in flight for this session is
    // reused, so a double auto-load (Vinnie view mounts twice in one frame)
    // never doubles the GET. Both callers await the same promise.
    if (loansLoading) return loansLoading;
    loansLoading = core.listLoans(get().token).finally(() => {
      loansLoading = null;
    });
    return loansLoading;
  },

  openLoan: async (amount) => {
    await get().ensureSession();
    set({ loading: true, error: null });
    try {
      const result = await core.openLoan(get().token, amount, core.newIdempotencyKey('loan'));
      get().applyState(result.state);
      trackEvent('loan_open', { amount: String(amount), source: 'core_session' });
      set({
        receipt: {
          kind: 'loan',
          status: 'ok',
          title: 'NOTE SIGNED',
          detail: `${COPY.loan.opened} // ${result.loan?.principal?.amount ?? amount} DCW_USD`,
          payload: result.loan,
        },
        loading: false,
      });
      return result;
    } catch (error) {
      set({ error: coreErrorMessage(error), loading: false });
      throw error;
    }
  },

  repayLoan: async (loanId, amount) => {
    await get().ensureSession();
    set({ loading: true, error: null });
    try {
      const result = await core.repayLoan(
        get().token,
        loanId,
        amount,
        core.newIdempotencyKey('repay'),
      );
      get().applyState(result.state);
      set({
        receipt: {
          kind: 'repay',
          status: 'ok',
          title: 'PRINCIPAL DOWN',
          detail: `${COPY.loan.repaid} // DUE ${result.loan?.amountDue?.amount ?? '--'}`,
          payload: result.loan,
        },
        loading: false,
      });
      return result;
    } catch (error) {
      set({ error: coreErrorMessage(error), loading: false });
      throw error;
    }
  },

  bribeHeat: async () => {
    await get().ensureSession();
    try {
      const result = await core.bribeHeat(get().token, core.newIdempotencyKey('bribe'));
      get().applyState(result.state);
      set({ receipt: { kind: 'bribe', status: 'ok', title: 'OFFICER PAID', detail: `${COPY.audit.bribed} // HEAT ${result.bribe?.heatAfter ?? '--'}`, payload: result.bribe } });
      return result;
    } catch (error) {
      set({ error: coreErrorMessage(error) });
      throw error;
    }
  },

  expandCoat: async () => {
    await get().ensureSession();
    try {
      const result = await core.expandCoat(get().token, core.newIdempotencyKey('coat'));
      get().applyState(result.state);
      set({ receipt: { kind: 'coat', status: 'ok', title: 'COAT EXPANDED', detail: `${COPY.coat.expanded} // CAP ${result.coat?.capacityAfter ?? '--'}`, payload: result.coat } });
      return result;
    } catch (error) {
      set({ error: coreErrorMessage(error) });
      throw error;
    }
  },

  loadHronir: async () => {
    await get().ensureSession();
    return core.listHronir(get().token);
  },

  loadDrops: async () => {
    await get().ensureSession();
    const { token } = get();
    if (!token) return [];
    const payload = await core.listDrops(token);
    const drops = Array.isArray(payload?.drops) ? payload.drops : [];
    set({ drops });
    return drops;
  },

  loadClaims: async (status = 'pending') => {
    await get().ensureSession();
    const { token } = get();
    if (!token) return [];
    const payload = await core.listClaims(token, { status });
    const claims = Array.isArray(payload?.claims) ? payload.claims : [];
    set({ claims });
    return claims;
  },

  claimPendingClaim: async (claimId) => {
    await get().ensureSession();
    try {
      const result = await core.claimPendingClaim(
        get().token,
        claimId,
        core.newIdempotencyKey('claim'),
      );
      const claim = result?.claim ?? null;
      if (claim) {
        set({
          claims: get().claims.map((row) => (row.claimId === claim.claimId ? claim : row)),
        });
      }
      set({
        receipt: {
          kind: 'claim',
          status: 'ok',
          title: 'CLAIM GRANTED',
          detail: `OFF-CHAIN // NO MINT (L9) // ${String(claimId).slice(-8)}`,
          payload: claim,
        },
      });
      return result;
    } catch (error) {
      set({ error: coreErrorMessage(error) });
      throw error;
    }
  },

  acquireHronir: async () => {
    await get().ensureSession();
    try {
      const result = await core.acquireHronir(get().token, core.newIdempotencyKey('hronir'));
      get().applyState(result.state);
      set({ receipt: { kind: 'hronir', status: 'ok', title: 'OBJECT SECURED', detail: COPY.hronir.acquired, payload: result.hronir } });
      return result;
    } catch (error) {
      set({ error: coreErrorMessage(error) });
      throw error;
    }
  },

  hronirVerb: async (hronirId, verb) => {
    await get().ensureSession();
    try {
      const result = await core.hronirVerb(
        get().token,
        hronirId,
        verb,
        core.newIdempotencyKey('hronir-verb'),
      );
      get().applyState(result.state);
      set({ receipt: { kind: 'hronir', status: 'ok', title: `OBJECT ${verb.toUpperCase()}`, detail: COPY.hronir[verb] || `OBJECT ${verb.toUpperCase()} FILED.`, payload: result } });
      return result;
    } catch (error) {
      set({ error: coreErrorMessage(error) });
      throw error;
    }
  },

  loadRuns: async (limit = 20) => {
    await get().ensureSession();
    return core.listRuns(get().token, limit);
  },

  loadStory: async () => {
    await get().ensureSession();
    return core.getStory(get().token);
  },

  startStory: async () => {
    await get().ensureSession();
    return core.startStory(get().token);
  },

  chooseStory: async (choiceIndex) => {
    await get().ensureSession();
    // The story choice can install apps server-side (install_app effects).
    // `/story/choose` returns story state, not player state, so snapshot the
    // desktop and refresh the authoritative `installedApps` before returning.
    const previousApps = getInstalledApps();
    const result = await core.chooseStory(get().token, choiceIndex);
    if (result.state && Array.isArray(result.state.installedApps)) {
      get().applyState(result.state);
    } else {
      try {
        await get().refreshState();
      } catch (error) {
        // Keep the story response usable if the follow-up read fails.
        if (result.state) get().applyState(result.state);
        set({ error: coreErrorMessage(error) });
      }
    }
    // ADR-0020: every install leaves a notification, including server installs.
    announceNewInstalls(previousApps, { from: result.story?.node?.speaker });
    return result;
  },

  loadMarketEvents: async () => {
    await get().ensureSession();
    return core.listMarketEvents(get().token);
  },

  loadEvents: async (limit = 100) => {
    await get().ensureSession();
    return core.listEvents(get().token, limit);
  },

  loadDayClose: async (seasonId, dayIndex) => {
    await get().ensureSession();
    return core.getDayClose(get().token, seasonId, dayIndex);
  },

  loadChatPresence: async () => {
    await get().ensureSession();
    return core.getChatPresence(get().token);
  },

  loadChatThreads: async () => {
    await get().ensureSession();
    return core.listChatThreads(get().token);
  },

  loadChatThread: async (characterId) => {
    await get().ensureSession();
    return core.getChatThread(get().token, characterId);
  },

  replyChatThread: async (characterId, body) => {
    await get().ensureSession();
    return core.replyChatThread(get().token, characterId, body);
  },
}));

export const ensureCoreSession = () => useCoreSession.getState().ensureSession();

export default useCoreSession;
